import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, DialogActions, DialogContent, Tabs, Tab } from '@mui/material';
import Typography from '@mui/material/Typography';

import type { UseQueryRefetch, User } from '../../../../../../types';

import {
  useDialog,
  useReportsGetDataFilterForSuperAdminQuery,
  useReportsGetDataFilterSubOwnersQuery,
  useReportsGetDataFilterEmployersQuery,
  useSnakeBar,
  useUserUpdateMutation,
} from '../../../../../../hooks';

import { TranslationKeys } from '../../../../../../translations';

import { StoreSelectors, useStoreSelector } from '../../../../../../store';

import { ActionButton, Dialog, HasRole, IsSuperAdmin } from '../../../../../../components';

import { UserAccessesListConfirmDialog, UserAccessesListEmployers, UserAccessesListSubOwners } from './components';
import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from './UserAccessesList.types';
import { UserEnvironmentsList } from '../UserEnvironmentsList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = React.memo(function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
});

type UserAccessesListProps = {
  user: User | undefined;
  isLoggedInUser?: boolean;
  openConfirmDeleteDialog: () => void;
  refetchGetUserQuery: UseQueryRefetch;
  onRequestAPIKey: () => void;
};

export const UserAccessesList = ({
  user,
  openConfirmDeleteDialog,
  refetchGetUserQuery,
  isLoggedInUser = false,
  onRequestAPIKey,
}: UserAccessesListProps) => {
  const { mutate: updateUserMutation, isLoading: isLoadingUserUpdateMutation } = useUserUpdateMutation();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const {
    dialogState: showConfirmDialog,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();

  const isSuperAdmin = IsSuperAdmin();
  const [userDefaultSelectedData, setUserDefaultSelectedData] = React.useState<UserAccessesListSelectedData>();
  const [selectedData, setSelectedData] = React.useState<UserAccessesListSelectedData>();
  const [confirmationDialog, setConfirmationDialog] = React.useState(false);
  const userId = useStoreSelector<string>(state => StoreSelectors.AppSelector.selectUserId(state.AppReducer));
  const hasAPIRole = HasRole('ROLE_API_API') && userId === user?.userId;

  React.useEffect(() => {
    setUserDefaultSelectedData(undefined);
    setSelectedData(undefined);
  }, [user?.userId]);

  const [subOwnersPage, setSubOwnersPage] = React.useState(1);
  const [subOwnersLimit, setSubOwnersLimit] = React.useState(25);
  const [subOwnersSearchQuery, setSubOwnersSearchQuery] = React.useState('');

  const [employersPage, setEmployersPage] = React.useState(1);
  const [employersLimit, setEmployersLimit] = React.useState(25);
  const [employersSearchQuery, setEmployersSearchQuery] = React.useState('');

  const {
    data: dataFilterForSuperAdmin,
    isLoading: isLoadingGetDataFilterForSuperAdminQuery,
    isFetching: isFetchingGetDataFilterForSuperAdminQuery,
  } = useReportsGetDataFilterForSuperAdminQuery({
    variables: { ownerId: user?.ownerId ?? '' },
    options: {
      enabled: isSuperAdmin && !!user?.ownerId,
    },
  });

  const {
    data: subOwnersData,
    isLoading: isLoadingSubOwnersQuery,
    isFetching: isFetchingSubOwnersQuery,
    refetch: refetchSubOwnersQuery,
  } = useReportsGetDataFilterSubOwnersQuery({
    variables: {
      userId,
      page: subOwnersPage,
      limit: subOwnersLimit,
      searchQuery: subOwnersSearchQuery || undefined,
    },
    options: {
      enabled: !isSuperAdmin && !!userId,
    },
  });

  const {
    data: employersData,
    isLoading: isLoadingEmployersQuery,
    isFetching: isFetchingEmployersQuery,
    refetch: refetchEmployersQuery,
  } = useReportsGetDataFilterEmployersQuery({
    variables: {
      userId,
      page: employersPage,
      limit: employersLimit,
      searchQuery: employersSearchQuery || undefined,
    },
    options: {
      enabled: !isSuperAdmin && !!userId,
    },
  });

  const refetchDataFilters = React.useCallback(() => {
    refetchSubOwnersQuery();
    refetchEmployersQuery();
  }, [refetchSubOwnersQuery, refetchEmployersQuery]);

  const dataFilter = React.useMemo(() => {
    if (isSuperAdmin) {
      return dataFilterForSuperAdmin;
    }

    return {
      '@id': subOwnersData?.['@id'] || employersData?.['@id'] || '/api/users/report/data-filter',
      '@type': subOwnersData?.['@type'] || employersData?.['@type'] || 'Report',
      ownerId: subOwnersData?.userId || employersData?.userId || userId,
      subOwners: subOwnersData?.subOwners,
      employers: employersData?.employers,
    };
  }, [dataFilterForSuperAdmin, subOwnersData, employersData, isSuperAdmin, userId]);

  React.useEffect(() => {
    if (!user) return;
    if (!dataFilter) return;

    if (userDefaultSelectedData) return;

    const accesses: UserAccessesListSelectedData = {
      employers: user?.accesses?.employers || {},
      subOwners: user?.accesses?.subOwners || {},
      eventFlows: [],
      areAllEmployersSelected: false,
      areAllSubOwnersSelected: false,
      autoAssignEmployers: user?.autoAssignEmployers || false,
    };

    if (Array.isArray(accesses.employers)) {
      accesses.employers = {};
    }
    if (Array.isArray(accesses.subOwners)) {
      accesses.subOwners = {};
    }

    if (user?.accesses?.subOwners === '*' && dataFilter?.subOwners) {
      accesses.subOwners = dataFilter.subOwners.reduce<Record<string, string>>((acc, subOwner) => {
        acc[subOwner.subOwnerId] = '*';
        return acc;
      }, {});
    }

    if (user?.accesses?.employers === '*' && dataFilter?.employers) {
      accesses.employers = dataFilter.employers.reduce<Record<string, string>>((acc, employer) => {
        acc[employer.employerId] = '*';
        return acc;
      }, {});
    }

    const eventFlows = [
      ...(dataFilter?.employers
        ?.filter(({ eventFlowDefaultUser }) => eventFlowDefaultUser === user?.userId)
        .map(employer => employer.employerId) || []),
      ...(dataFilter?.subOwners?.flatMap(subOwner =>
        subOwner.employers
          .filter(({ eventFlowDefaultUser }) => eventFlowDefaultUser === user?.userId)
          .map(employer => employer.employerId),
      ) || []),
    ];
    accesses.eventFlows = eventFlows;

    const defaultAccesses = JSON.parse(JSON.stringify(accesses));

    setSelectedData(accesses);
    setUserDefaultSelectedData(defaultAccesses);
  }, [user, dataFilter, userDefaultSelectedData]);

  const hasChangedAccesses = React.useMemo(() => {
    const {
      areAllEmployersSelected: defaultAreAllEmployersSelected,
      areAllSubOwnersSelected: defaultAreAllSubOwnersSelected,
      ...defaultData
    } = userDefaultSelectedData || {};

    const {
      areAllEmployersSelected: selectedAreAllEmployersSelected,
      areAllSubOwnersSelected: selectedAreAllSubOwnersSelected,
      ...currentSelectedData
    } = selectedData || {};

    const userAccesses = JSON.stringify(defaultData || {});
    const selectedAccesses = JSON.stringify(currentSelectedData || {});

    return userAccesses !== selectedAccesses;
  }, [userDefaultSelectedData, selectedData]);

  const onClickCancelAccessesChanges = React.useCallback(() => {
    setSelectedData(userDefaultSelectedData);
  }, [userDefaultSelectedData]);

  const onChangeSelected = React.useCallback<UserAccessesListOnChangeSelected>(newSelectedData => {
    setSelectedData(newSelectedData);
  }, []);

  const updateUserAccesses = React.useCallback(() => {
    if (user && selectedData) {
      const method = 'updateUser';

      updateUserMutation(
        {
          userId: user.userId,
          ownerId: user.ownerId || null,
          roleId: user.roleId,
          firstName: user.personName.firstName,
          middleName: user.personName.middleName,
          lastName: user.personName.lastName,
          emailAddress: user.emailAddress,
          language: user.language,
          internal: user.internal,
          accesses: {
            employers: selectedData.employers,
            subOwners: selectedData.subOwners,
          },
          eventFlows: selectedData.eventFlows,
          autoAssignEmployers: selectedData.autoAssignEmployers,
        },
        {
          onSuccess: () => {
            closeConfirmDialog();
            refetchGetUserQuery();
            refetchDataFilters();
            showSuccessSnakeBar({ method });
          },
          onError: (error: any) => {
            showErrorSnakeBar({ method, message: error?.message });
          },
        },
      );
    }
  }, [
    closeConfirmDialog,
    refetchDataFilters,
    refetchGetUserQuery,
    selectedData,
    showErrorSnakeBar,
    showSuccessSnakeBar,
    updateUserMutation,
    user,
  ]);

  const isGetDataFilterForSuperAdminQueryLoading =
    isSuperAdmin && (isLoadingGetDataFilterForSuperAdminQuery || isFetchingGetDataFilterForSuperAdminQuery);

  const isSubOwnersQueryLoading = !isSuperAdmin && (isLoadingSubOwnersQuery || isFetchingSubOwnersQuery);
  const isEmployersQueryLoading = !isSuperAdmin && (isLoadingEmployersQuery || isFetchingEmployersQuery);

  const isLoading =
    isGetDataFilterForSuperAdminQueryLoading ||
    isSubOwnersQueryLoading ||
    isEmployersQueryLoading ||
    isLoadingUserUpdateMutation;

  const [tabValue, setValue] = React.useState(0);

  const handleChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  }, []);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleChange} aria-label="basic tabs example">
          <Tab label="Employers and Subowners" id={'simple-tab-1'} />
          {isSuperAdmin && <Tab label="Environments" id={'simple-tab-2'} />}
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={'h6'}>
            <FormattedMessage id={TranslationKeys.users_accessesList_headerText} />
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            {hasAPIRole && (
              <ActionButton
                variant={'outlined'}
                onClick={onRequestAPIKey}
                disabled={isLoading}
                messageId={TranslationKeys.button_requestAPI}
              />
            )}
            <ActionButton
              variant={'outlined'}
              messageId={TranslationKeys.button_delete}
              onClick={openConfirmDeleteDialog}
              disabled={isLoggedInUser || isLoading}
            />
          </Box>
        </Box>
        <UserAccessesListSubOwners
          isLoading={isSubOwnersQueryLoading || isLoadingUserUpdateMutation}
          isLoggedInUser={isLoggedInUser}
          onChangeSelected={onChangeSelected}
          selectedData={selectedData}
          subOwners={dataFilter?.subOwners}
          selectedEventFlows={selectedData?.eventFlows || []}
          openConfirmationDialog={setConfirmationDialog}
          topLevelEmployers={dataFilter?.employers}
          page={subOwnersPage}
          limit={subOwnersLimit}
          totalItems={subOwnersData?.totalItems ?? 0}
          totalPages={subOwnersData?.totalPages ?? 0}
          onPageChange={setSubOwnersPage}
          onLimitChange={setSubOwnersLimit}
          searchQuery={subOwnersSearchQuery}
          onSearchQueryChange={setSubOwnersSearchQuery}
          isSuperAdmin={isSuperAdmin}
        />
        <UserAccessesListEmployers
          isLoading={isEmployersQueryLoading || isLoadingUserUpdateMutation}
          isLoggedInUser={isLoggedInUser}
          onChangeSelected={onChangeSelected}
          selectedData={selectedData}
          employers={dataFilter?.employers}
          selectedEventFlows={selectedData?.eventFlows || []}
          openConfirmationDialog={setConfirmationDialog}
          subOwners={dataFilter?.subOwners}
          page={employersPage}
          limit={employersLimit}
          totalItems={employersData?.totalItems ?? 0}
          totalPages={employersData?.totalPages ?? 0}
          onPageChange={setEmployersPage}
          onLimitChange={setEmployersLimit}
          searchQuery={employersSearchQuery}
          onSearchQueryChange={setEmployersSearchQuery}
          isSuperAdmin={isSuperAdmin}
        />

        {hasChangedAccesses && (
          <Box marginTop={3} marginBottom={3} display={'flex'} justifyContent={'end'} alignItems={'center'} gap={2}>
            <ActionButton
              variant={'outlined'}
              messageId={TranslationKeys.button_cancel}
              onClick={onClickCancelAccessesChanges}
              disabled={isLoggedInUser || isLoading}
            />
            <ActionButton
              messageId={TranslationKeys.button_save}
              onClick={openConfirmDialog}
              disabled={isLoggedInUser || isLoading}
            />
          </Box>
        )}

        {showConfirmDialog && !!selectedData && (
          <UserAccessesListConfirmDialog
            show={showConfirmDialog}
            isLoading={isLoading}
            onClose={closeConfirmDialog}
            onConfirm={updateUserAccesses}
            selectedData={selectedData}
            data={dataFilter}
          />
        )}
        <Dialog
          open={confirmationDialog}
          title={<FormattedMessage id={TranslationKeys.users_accessesList_dialog_title} />}
          onClose={() => {
            setConfirmationDialog(false);
          }}
        >
          <DialogContent>
            <Typography variant={'body1'}>
              <FormattedMessage id={TranslationKeys.users_accessesList_dialog_content} />
            </Typography>
          </DialogContent>
          <DialogActions>
            <ActionButton
              messageId={TranslationKeys.button_confirm}
              onClick={() => {
                setConfirmationDialog(false);
              }}
            />
          </DialogActions>
        </Dialog>
      </CustomTabPanel>
      {isSuperAdmin && (
        <CustomTabPanel value={tabValue} index={1}>
          <UserEnvironmentsList user={user} refetchGetUserQuery={refetchGetUserQuery}></UserEnvironmentsList>
        </CustomTabPanel>
      )}
    </>
  );
};
